import { Link } from 'react-router-dom';
import { AdSlot } from '../components/AdSlot';
import { AXIS_LABELS, jungianType } from '../diagnosis/labels';

/**
 * ユングのタイプ論と、この診断の型コードの読み方（`/help/jung`）。
 *
 * 結果ページの「どう測ったか」に「ユングの類型でいえば主機能は〜型、補助機能は〜」と出るので、
 * その言葉の意味と、4 文字の型からどう読んでいるかをここで説明する。
 *
 * 書き方の方針は `/help` と同じ。断定せず、できないことはできないと書く。
 * ユングの類型は学術上の分類なので名前を出せるが、後発の商標名（4 文字の型を売る各社の検査名）は使わない
 * （doc/ASSESSMENT.md §6.4）。測定精度が未検証であること（doc/ASSESSMENT.md §0）はここでも省かない。
 * 16 型の対応表は結果ページと同じ `jungianType` から作り、二重に書かない。
 */

const FUNCTIONS: readonly { readonly name: string; readonly kind: '知覚' | '判断'; readonly letter: string; readonly body: string }[] = [
  { name: '感覚', kind: '知覚', letter: 'S', body: 'いま目の前にあるもの、実際に起きていること、五感で確かめられることを手がかりにする。' },
  { name: '直観', kind: '知覚', letter: 'N', body: '見えているものの向こうにある可能性やつながり、これから起きそうなことを手がかりにする。' },
  { name: '思考', kind: '判断', letter: 'T', body: '筋道が通っているか、原則に合っているかで決める。' },
  { name: '感情', kind: '判断', letter: 'F', body: '自分や周りの人にとって大切かどうか、価値に合うかどうかで決める。' },
];

/** 8 つの型。ユングの説明を、断定しない現代の言葉に言い換えたもの。 */
const EIGHT_TYPES: readonly { readonly name: string; readonly body: string }[] = [
  { name: '外向的思考型', body: '外の世界の事実や決まりを筋道立てて整え、計画や仕組みにして動かそうとする傾向。' },
  { name: '内向的思考型', body: '外からの情報より、自分の中で考えを組み立てて納得することを大切にする傾向。' },
  { name: '外向的感情型', body: '周りの人との関係や場の調和を読み、人と人をつなぐ方向に動く傾向。' },
  { name: '内向的感情型', body: '外にはあまり出さないが、自分の中の価値観や大切なものをはっきり持ち、それに沿って選ぶ傾向。' },
  { name: '外向的感覚型', body: 'いま起きていることを五感でとらえ、体験そのものを味わい、その場で対応する傾向。' },
  { name: '内向的感覚型', body: '経験して確かめたこと、積み重ねてきた記憶や実績を頼りに、着実に進める傾向。' },
  { name: '外向的直観型', body: '新しい可能性や機会を外の世界に見つけ、次から次へと試してみたくなる傾向。' },
  { name: '内向的直観型', body: '物事の奥にある意味やこれから起きる流れを、自分の中でじっくりとらえようとする傾向。' },
];

/** 16 型の対応表。並びは結果ページと同じ規則（`jungianType`）で作る。 */
const SIXTEEN: readonly { readonly code: string; readonly dominant: string; readonly auxiliary: string }[] = (() => {
  const rows: { code: string; dominant: string; auxiliary: string }[] = [];
  for (const a of ['E', 'I']) for (const p of ['S', 'N']) for (const j of ['T', 'F']) for (const o of ['J', 'P']) {
    const code = `${a}${p}${j}${o}`;
    const jung = jungianType(code);
    if (jung) rows.push({ code, dominant: jung.dominant, auxiliary: jung.auxiliary });
  }
  return rows;
})();

export function JungHelpPage() {
  const mainAxes = AXIS_LABELS.slice(0, 4);
  return (
    <main className="page help">
      <p className="page__kicker">ヘルプ</p>
      <h1 className="page__title">ユングのタイプ論と、この診断の型の読み方</h1>
      <p className="page__lead">
        結果ページの「どう測ったか」に、「ユングの類型でいえば主機能は〜型、補助機能は〜」という一文が出ます。ここでは、その言葉が何を指しているのか、この診断の 4 文字の型からどう読んでいるのかを説明します。よくある質問は<Link to="/help">ヘルプ</Link>にあります。
      </p>

      <nav className="help__toc" aria-label="目次">
        <a className="chip" href="#theory">タイプ論とは</a>
        <a className="chip" href="#letters">4 文字の読み方</a>
        <a className="chip" href="#eight">8 つの型</a>
        <a className="chip" href="#sixteen">16 型の対応表</a>
        <a className="chip" href="#limits">気をつけてほしいこと</a>
      </nav>

      <section className="section" id="theory">
        <h2 className="section__title">ユングのタイプ論とは</h2>
        <div className="help__a">
          <p>
            スイスの精神科医カール・グスタフ・ユング（1875–1961）が、1921 年の『心理学的類型』で示した、人の心の働き方の分け方です。ユングは、人が世界と関わるときの<strong>態度</strong>を 2 つ、心が働くときの<strong>機能</strong>を 4 つに分けました。
          </p>
          <p>
            <strong>態度</strong>は、関心とエネルギーが外の世界（人・もの・出来事）へ向きやすい<strong>外向</strong>と、自分の内側（考え・感じ方・イメージ）へ向きやすい<strong>内向</strong>です。どちらか一方しか持たないのではなく、どちらが自然に出やすいか、という話です。
          </p>
          <p><strong>機能</strong>は、情報を受け取る 2 つ（知覚機能）と、それをもとに決める 2 つ（判断機能）です。</p>
          <table className="help__table">
            <thead>
              <tr><th>機能</th><th>働き</th><th>この診断の文字</th><th>どういうことか</th></tr>
            </thead>
            <tbody>
              {FUNCTIONS.map((f) => (
                <tr key={f.name}><td><strong>{f.name}</strong></td><td className="help__nowrap">{f.kind}</td><td className="typecode">{f.letter}</td><td>{f.body}</td></tr>
              ))}
            </tbody>
          </table>
          <p>
            ユングは、この 4 つのうち、その人がいちばん自然に使い、頼りにしている機能を<strong>主機能</strong>と呼びました。主機能に外向・内向の態度を掛け合わせると、外向的思考型・内向的直観型のように <strong>8 つの型</strong>になります。主機能を支えるもう 1 つの機能が<strong>補助機能</strong>で、主機能が知覚機能なら補助機能は判断機能、主機能が判断機能なら補助機能は知覚機能になります。補助機能の態度は主機能と逆に働く、と考えられています。
          </p>
        </div>
      </section>

      <section className="section" id="letters">
        <h2 className="section__title">4 文字の型は、ここからどう読むか</h2>
        <div className="help__a">
          <p>
            ユングの 8 つの型は、のちに「どの機能を外の世界に向けているか」を示す 4 つ目の分け方（J／P）を加えて、4 文字で表す体系に発展しました。この診断の主軸 4 本も、その 4 文字に対応しています。
          </p>
          <table className="help__table">
            <thead>
              <tr><th>文字</th><th>測っていること</th><th>ユングの言葉でいえば</th></tr>
            </thead>
            <tbody>
              {mainAxes.map((axis) => (
                <tr key={axis.axis}>
                  <td className="typecode">{axis.low.code}／{axis.high.code}</td>
                  <td>{axis.caption}（{axis.low.name}／{axis.high.name}）</td>
                  <td>
                    {axis.axis === 'EI' && '態度。外向か内向か'}
                    {axis.axis === 'SN' && '知覚機能。感覚と直観のどちらを頼るか'}
                    {axis.axis === 'TF' && '判断機能。思考と感情のどちらで決めるか'}
                    {axis.axis === 'JP' && '外の世界に向けるのが判断機能（J）か知覚機能（P）か。ユングの元の分け方には無く、あとから加わった'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>4 文字から主機能と補助機能を読む手順は、次のとおりです。</p>
          <ol>
            <li>J なら判断機能（T か F）、P なら知覚機能（S か N）を<strong>外の世界に向けている</strong>と読みます。</li>
            <li><strong>外向（E）の人</strong>は、外に向けている機能がそのまま主機能です。もう一方が補助機能で、内向きに働きます。</li>
            <li><strong>内向（I）の人</strong>は、外に向けている機能は補助機能で、<strong>内側で使っているもう一方が主機能</strong>です。</li>
          </ol>
          <p>
            たとえば INTJ は、J なので判断機能（T）を外に向けています。内向なので、それは補助機能（外向的思考）で、主機能は内側で使っている直観、つまり<strong>内向的直観型</strong>と読みます。
            ESFP は、P なので知覚機能（S）を外に向けていて、外向なのでそれが主機能、<strong>外向的感覚型</strong>（補助機能は内向的感情）です。
          </p>
          <p>
            結果ページの文はこの手順で作っています。副軸の 2 本（主張型／受容型、泰然型／繊細型）はユングの類型には無い分け方なので、この読みには使いません。
            8 人・4 人の診断では型が 4 文字そろわないので、この一文は出ません。
          </p>
        </div>
      </section>

      <section className="section" id="eight">
        <h2 className="section__title">8 つの型</h2>
        <div className="help__a">
          <p>ユングの説明を、いまの言葉に短く言い換えたものです。どれも「そういう傾向が出やすい」という意味で、人をどれか 1 つに閉じ込めるものではありません。</p>
          <dl className="help__list">
            {EIGHT_TYPES.map((t) => (
              <div key={t.name} className="help__item">
                <dt className="help__q">{t.name}</dt>
                <dd className="help__a"><p>{t.body}</p></dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="section" id="sixteen">
        <h2 className="section__title">16 型の対応表</h2>
        <div className="help__a">
          <p>この診断の 4 文字と、ユングの類型でいえばどれに当たるかの対応です。結果ページに出る一文は、この表と同じ規則で作られています。</p>
          <table className="help__table">
            <thead>
              <tr><th>型</th><th>主機能</th><th>補助機能</th></tr>
            </thead>
            <tbody>
              {SIXTEEN.map((row) => (
                <tr key={row.code}><td className="typecode">{row.code}</td><td>{row.dominant}型</td><td>{row.auxiliary}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="section" id="limits">
        <h2 className="section__title">気をつけてほしいこと</h2>
        <div className="help__a">
          <p>
            <strong>ユングの類型は、観察から立てた「理念型」です。</strong>ユング自身も、純粋な型の人はいないと書いています。この診断は、軸ごとに 0〜100 の連続したスコアで測り、型はその要約として添えています。スコアが真ん中に近い軸では、少しの違いで反対の文字になります。結果ページの「確からしさ」を合わせて見てください。
          </p>
          <p>
            <strong>研究の裏付けについて。</strong>人の性格を 4 つの機能で分ける考え方は、現代の性格心理学では主流ではありません。いまの研究で最も裏付けの多いのは、性格を 5 つの因子（いわゆるビッグファイブ）で連続的にとらえる見方です。この診断の軸は、その 5 因子とその側面に対応づけて設計しています。ユングの類型は、結果を言葉にするための「もうひとつの読み方」として添えているものです。
          </p>
          <p>
            <strong>この診断は専門家の検査ではありません。</strong>信頼性や妥当性の数値はまだ実測できておらず、採用や選抜の判断に使えるだけの精度は確かめられていません。自分を言葉にするきっかけとして、遊びとしてお使いください。
          </p>
          <p>
            参考: C. G. ユング『心理学的類型』（1921）。日本語訳は複数あります。
          </p>
        </div>
      </section>

      <section className="section">
        <div className="page__actions">
          <Link className="button button--primary" to="/help">ヘルプに戻る</Link>
          <Link className="button button--ghost" to="/">トップへ戻る</Link>
          <Link className="button button--ghost" to="/contact">お問い合わせ</Link>
        </div>
      </section>
      <AdSlot placement="help" />
    </main>
  );
}
